import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller } from 'react-hook-form'

import { FormHelperText, Grid, Typography } from '@barracuda-internal/bds-core'

// TODO: eliminate imports from "old" code paths (components/..., lib/...), move things to V2
// V1 imports
import { useFormatMessage } from 'lib/localization'
import outboundSettingsStyles from 'components/pages/outboundSettings/outboundSettingsStyles'
import SenderNotification from 'components/pages/support/help/senderNotification/SenderNotification'
import { GROUP_TITLE, TOPICS, useSupportModalLogic } from 'components/pages/support/useSupportModalLogic'
import ScheduleMatrix from 'components/libs/scheduler/ScheduleMatrix'
import {
  PageTitle,
  FormSpacer,
  PageSubtitle,
  FormRow,
  InlineRadioGroup,
  InlineInputField
} from 'components/libs/layout/pages/pageLayout'
import { SettingsPageContainer } from 'components/libs/layout/pages/SettingsPageContainer'
import { useOutboundSettingsRights } from 'components/libs/userRights/pages/useOutboundSettingsRights'
import WarningDialog from 'components/libs/dialog/warningDialog/WarningDialog'
import { useAppDispatch } from 'redux/toolkit/hooks'
import { setErrorSnackBar, setSuccessSnackBar } from 'redux/features/app/appSlice'

// V2 module imports
import {
  useForm,
  OutboundQuarantineInterval,
  RadioButtonStates,
  useFormState,
  useI18n,
  IEgdApiClient
} from 'v2/modules/lib'
import { INotificationsForm } from './notificationsForm'

const BASE_I18N_KEY = 'ess.outbound_settings.notifications'
const DIALOG_I18N_KEY = 'ess.dialog'
export interface NotificationsPageProps {
  domainId: number | null
  settingsForm: INotificationsForm
  egdApiClient: IEgdApiClient
  accountId: string
}

export const NotificationsPage: React.FC<NotificationsPageProps> = ({
  accountId,
  domainId,
  settingsForm,
  egdApiClient
}) => {
  const dispatch = useAppDispatch()
  const classes = outboundSettingsStyles()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatDialogMessage = useFormatMessage(DIALOG_I18N_KEY)
  const [onOpenSupport, HelpModal] = useSupportModalLogic({
    content: SenderNotification,
    title: TOPICS.notifications,
    groupTitle: GROUP_TITLE.outboundSettings
  })
  const { canEditSpamNotificationAddress } = useOutboundSettingsRights()

  const canSetState = useRef(true)
  const [isVerifyingDomainConflicts, setIsVerifyingDomainConflicts] = useState(false)
  const [isDomainConflictDialogOpen, setIsDomainConflictDialogOpen] = useState(false)
  const [numberOfConflictingDomains, setNumberOfConflictingDomains] = useState(0)
  const [isResetPending, setIsResetPending] = useState(false)
  const { t } = useI18n()
  const form = useForm({
    initialData: settingsForm.form.getInitialValues(),
    load: async () => settingsForm.form.load(),
    validate: async () => {
      // Not implemented yet
    },
    save: async (dirtyFormData: Record<string, any>) => {
      try {
        await settingsForm.form.save(dirtyFormData)
        dispatch(setSuccessSnackBar({ message: 'success_changing_settings' }))
      } catch (e) {
        dispatch(setErrorSnackBar({ message: 'error_changing_settings' }))
      }
    },
    schema: settingsForm.schema
  })
  const formState = useFormState(form)
  const isInProgress = useMemo(
    () => formState.isLoading || !formState.isReady,
    [formState.isLoading, formState.isReady]
  )

  // TODO: porting the module to a headless app cleans up extensive inline code in this UI component
  const onClickSave = useCallback(async () => {
    try {
      if (!domainId) {
        setIsVerifyingDomainConflicts(true)
        const domainModifiedSettings = await egdApiClient.accountService.getDomainModifiedSettings(accountId)
        const conflictingDomainIds: number[] = []
        Object.keys(formState.dirtyFields).forEach(inputName => {
          const inputAdapters = settingsForm.form.adapters.getByInputName(inputName)
          inputAdapters.forEach(adapter => {
            const nextList: number[] = domainModifiedSettings[adapter.variable] || []
            conflictingDomainIds.push(...nextList)
          })
        })
        if (!canSetState.current) {
          return
        }
        setIsVerifyingDomainConflicts(false)
        if (new Set(conflictingDomainIds).size) {
          setIsDomainConflictDialogOpen(true)
          setNumberOfConflictingDomains(new Set(conflictingDomainIds).size)
          return
        }
      }
      await form.save()
    } catch {
      if (!canSetState.current) {
        return
      }
      setIsVerifyingDomainConflicts(false)
      dispatch(setErrorSnackBar({ message: 'error_changing_settings' }))
    }
  }, [
    accountId,
    dispatch,
    domainId,
    egdApiClient.accountService,
    form,
    formState.dirtyFields,
    settingsForm.form.adapters
  ])

  useEffect(() => {
    canSetState.current = true
    return () => {
      canSetState.current = false
    }
  }, [])

  return useMemo(
    () => (
      <SettingsPageContainer
        isContentLoading={isInProgress}
        isFailedToLoad={formState.isFailed}
        dirtyCheck
        isDirtyForm={formState.isDirty}
      >
        <WarningDialog
          open={isDomainConflictDialogOpen}
          title={formatDialogMessage('domain_settings_conflict', { numberOfDomains: numberOfConflictingDomains })}
          description={formatDialogMessage('domain_settings_conflict_description')}
          onCancel={() => {
            setIsDomainConflictDialogOpen(false)
            setNumberOfConflictingDomains(0)
          }}
          onConfirm={async () => {
            await form.save()
            if (canSetState.current) {
              setIsDomainConflictDialogOpen(false)
              setNumberOfConflictingDomains(0)
            }
          }}
          isPending={formState.isSaving}
        />
        <HelpModal />
        <PageTitle
          title={formatMessage('title')}
          onOpenSupport={onOpenSupport}
          onCancel={() => setIsResetPending(true)}
          onReset={() => form.reset(formState.defaultValues)}
          isResetPending={isResetPending}
          closeWarningDialog={() => setIsResetPending(false)}
          onSubmit={onClickSave}
          submitInProgress={formState.isSaving || isVerifyingDomainConflicts}
          isDisabledSubmit={!formState.isDirty || isInProgress || settingsForm.form.isReadonly}
        />
        <FormSpacer />
        <PageSubtitle>
          {formatMessage('admin_quarantine_notification')}
          <Typography variant="body1" className={classes.italic} data-testid="quarantine-description">
            {formatMessage('admin_quarantine_notification_description')}
          </Typography>
        </PageSubtitle>
        <FormSpacer />
        <FormRow rowTitle={formatMessage('notification_interval')}>
          <Controller
            control={form.control}
            name="outboundQuarantineInterval"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineRadioGroup
                  name="outboundQuarantineInterval"
                  value={value}
                  onChange={onChange}
                  disabled={isInProgress || settingsForm.form.isReadonly}
                  values={[
                    OutboundQuarantineInterval.NEVER,
                    OutboundQuarantineInterval.IMMEDIATELY,
                    OutboundQuarantineInterval.CUSTOM
                  ]}
                  labels={[formatMessage('never'), formatMessage('immediately'), formatMessage('scheduled')]}
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>
        <FormSpacer />
        {formState.values.outboundQuarantineInterval === OutboundQuarantineInterval.CUSTOM && (
          <FormRow rowTitle={formatMessage('schedule_notification_intervals')}>
            <Grid item xs={5}>
              <Controller
                control={form.control}
                name="customOutboundSchedule"
                render={({ field: { onChange, value }, fieldState }) => (
                  <>
                    <ScheduleMatrix
                      isEnabled={
                        formState.values.outboundQuarantineInterval === OutboundQuarantineInterval.CUSTOM &&
                        !settingsForm.form.isReadonly
                      }
                      customInboundSchedule={{ ...value }}
                      doUpdate={schedule => {
                        onChange({ target: { value: schedule } })
                      }}
                    />
                    {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
                  </>
                )}
              />
              <Typography variant="body2" className={classes.matrixDescription}>
                {formatMessage('matrix_description', {
                  br: () => <br />
                })}
              </Typography>
            </Grid>
          </FormRow>
        )}
        <FormSpacer />
        <FormRow rowTitle={formatMessage('notification_address')}>
          <Controller
            control={form.control}
            name="outboundEmailIds"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineInputField
                  inputClassName={classes.mediumInput}
                  name="outboundEmailIds"
                  onChange={onChange}
                  value={value}
                  disabled={
                    formState.values.outboundQuarantineInterval === OutboundQuarantineInterval.NEVER ||
                    settingsForm.form.isReadonly
                  }
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>
        <FormSpacer />
        <FormSpacer />
        <PageSubtitle>
          {formatMessage('sender_quarantine_notification')}
          <Typography variant="body1" className={classes.italic} data-testid="sender-quarantine-description">
            {formatMessage('sender_quarantine_notification_description')}
          </Typography>
        </PageSubtitle>
        <FormSpacer />
        <FormRow rowTitle={formatMessage('quarantine_sender_notification')}>
          <Controller
            control={form.control}
            name="senderNotificationEnable"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineRadioGroup
                  name="senderNotificationEnable"
                  value={value}
                  onChange={onChange}
                  disabled={isInProgress || settingsForm.form.isReadonly}
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>
        <FormSpacer />
        <FormRow rowTitle={formatMessage('quarantine_notification_address')}>
          <Controller
            control={form.control}
            name="senderNotificationAddress"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineInputField
                  inputClassName={classes.mediumInput}
                  name="senderNotificationAddress"
                  onChange={onChange}
                  value={value}
                  disabled={
                    formState.values.senderNotificationEnable === RadioButtonStates.off || settingsForm.form.isReadonly
                  }
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>
        <FormSpacer />
        <FormRow rowTitle={formatMessage('quarantine_notification_subject')}>
          <Controller
            control={form.control}
            name="senderNotificationSubject"
            render={({ field: { onChange, value } }) => (
              <InlineInputField
                inputClassName={classes.mediumInput}
                name="senderNotificationSubject"
                onChange={onChange}
                value={value}
                placeholder={formatMessage('quarantine_notification_subject_placeholder')}
                disabled={
                  formState.values.senderNotificationEnable === RadioButtonStates.off || settingsForm.form.isReadonly
                }
              />
            )}
          />
        </FormRow>
        <FormSpacer />
        <FormRow alignTitleToTop rowTitle={formatMessage('quarantine_notification_template')}>
          <Controller
            control={form.control}
            name="senderNotificationTemplate"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineInputField
                  inputClassName={classes.mediumArea}
                  name="senderNotificationTemplate"
                  onChange={onChange}
                  value={value}
                  placeholder={formatMessage('quarantine_notification_template_placeholder')}
                  disabled={
                    formState.values.senderNotificationEnable === RadioButtonStates.off || settingsForm.form.isReadonly
                  }
                  rows={9}
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>
        <FormSpacer />
        <FormSpacer />
        <PageSubtitle>
          {formatMessage('notification_to_sender')}
          <Typography variant="body1" className={classes.italic} data-testid="notification-sender-description">
            {formatMessage('notification_to_sender_description')}
          </Typography>
        </PageSubtitle>
        <FormSpacer />
        <FormRow rowTitle={formatMessage('reject_notification_address')}>
          <Controller
            control={form.control}
            name="rejectNotificationAddress"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineInputField
                  inputClassName={classes.mediumInput}
                  name="rejectNotificationAddress"
                  onChange={onChange}
                  value={value}
                  disabled={isInProgress || settingsForm.form.isReadonly}
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>
        <FormSpacer />
        <FormRow rowTitle={formatMessage('reject_notification_subject')}>
          <Controller
            control={form.control}
            name="rejectNotificationSubject"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineInputField
                  inputClassName={classes.mediumInput}
                  name="rejectNotificationSubject"
                  onChange={onChange}
                  value={value}
                  placeholder={formatMessage('reject_notification_subject_placeholder')}
                  disabled={isInProgress || settingsForm.form.isReadonly}
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>
        <FormSpacer />
        <FormRow alignTitleToTop rowTitle={formatMessage('reject_notification_template')}>
          <Controller
            control={form.control}
            name="rejectNotificationTemplate"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineInputField
                  inputClassName={classes.mediumArea}
                  name="rejectNotificationTemplate"
                  onChange={onChange}
                  value={value}
                  placeholder={formatMessage('reject_notification_template_placeholder')}
                  rows={6}
                  disabled={isInProgress || settingsForm.form.isReadonly}
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>
        {canEditSpamNotificationAddress && !domainId && (
          <>
            <FormSpacer />
            <FormSpacer />
            <PageSubtitle>
              {formatMessage('admin_spam_notification')}
              <Typography variant="body1" className={classes.italic} data-testid="admin-spam-description">
                {formatMessage('admin_spam_notification_description')}
              </Typography>
            </PageSubtitle>
            <FormSpacer />
            <FormRow rowTitle={formatMessage('spam_notification_address')}>
              <Controller
                control={form.control}
                name="outboundSpamNotificationAddress"
                render={({ field: { onChange, value }, fieldState }) => (
                  <>
                    <InlineInputField
                      inputClassName={classes.mediumInput}
                      name="outboundSpamNotificationAddress"
                      onChange={onChange}
                      value={value}
                      disabled={isInProgress || settingsForm.form.isReadonly}
                    />
                    {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
                  </>
                )}
              />
            </FormRow>
          </>
        )}
        <FormSpacer />
        <FormSpacer />
        <FormSpacer />
      </SettingsPageContainer>
    ),
    [
      isInProgress,
      formState.isFailed,
      formState.isDirty,
      formState.isSaving,
      formState.values.outboundQuarantineInterval,
      formState.values.senderNotificationEnable,
      formState.defaultValues,
      isDomainConflictDialogOpen,
      formatDialogMessage,
      numberOfConflictingDomains,
      HelpModal,
      formatMessage,
      onOpenSupport,
      isResetPending,
      onClickSave,
      isVerifyingDomainConflicts,
      settingsForm.form.isReadonly,
      classes.italic,
      classes.matrixDescription,
      classes.mediumInput,
      classes.mediumArea,
      form,
      canEditSpamNotificationAddress,
      domainId,
      t
    ]
  )
}
